import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LemurClientStatus, LemurMode } from '@vsolv/packages/lemur/domain';
import { firstValueFrom } from 'rxjs';

interface FeatureFlagsResponse {
  feature_flags: Record<string, unknown>;
}

const LEMUR_FINANCING_ENABLED_FLAG = 'LEMUR_FINANCING_ENABLED';

@Injectable({ providedIn: 'root' })
export class LemurConfigWebService {
  private readonly http = inject(HttpClient);

  getStatus() {
    return firstValueFrom(this.http.get<LemurClientStatus>('/api/lemur/config'));
  }

  setApiKey(apiKey: string, environment?: LemurMode) {
    return firstValueFrom(this.http.post<LemurClientStatus>('/api/lemur/config', { apiKey, environment }));
  }

  setEnvironment(environment: LemurMode) {
    return firstValueFrom(this.http.put<LemurClientStatus>('/api/lemur/config/environment', { environment }));
  }

  async setEnabled(enabled: boolean): Promise<LemurClientStatus> {
    // Keep checkout gating fail-closed if either request fails: configure Lemur before
    // exposing it on enable, and remove the checkout flag before disabling its config.
    if (enabled) {
      const status = await this.setConfigEnabled(true);
      await this.setFeatureFlag(true);
      return status;
    }

    await this.setFeatureFlag(false);
    return this.setConfigEnabled(false);
  }

  async deleteApiKey(): Promise<LemurClientStatus> {
    await this.setFeatureFlag(false);
    return firstValueFrom(this.http.delete<LemurClientStatus>('/api/lemur/config'));
  }

  private setConfigEnabled(enabled: boolean): Promise<LemurClientStatus> {
    return firstValueFrom(this.http.put<LemurClientStatus>('/api/lemur/config/enabled', { enabled }));
  }

  private setFeatureFlag(enabled: boolean): Promise<FeatureFlagsResponse> {
    return firstValueFrom(
      this.http.post<FeatureFlagsResponse>('/api/feature-flags', {
        feature_flags: { [LEMUR_FINANCING_ENABLED_FLAG]: enabled },
      })
    );
  }
}
